import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, inject, Input, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { isValidPhoneNumber } from 'libphonenumber-js';
import { NgxMaterialIntlTelInputComponent } from 'ngx-material-intl-tel-input';
import { DataService } from 'projects/ngx-messaging/src/core/services/data.service';
import { NgxMessagingSharedModule } from 'projects/ngx-messaging/src/modules/shared/shared.module';
import { DarkModeService, LoggerService, skipApiKey, SnackBarService } from 'projects/ngx-messaging/src/public-api';
import { catchError, map, Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ISignatureData, ISignatureHeaders, ISignaturePayload, IUrlGeneratorFormParams } from './IURLGenerator';

function validPhoneNumber(control: AbstractControl): ValidationErrors | null {
  const value = typeof control.value === 'string' ? control.value.trim() : '';
  if (!value || isValidPhoneNumber(value)) {
    return null;
  }
  return { phone: true };
}

@Component({
  selector: 'app-url-generator',
  templateUrl: './url-generator.component.html',
  styleUrls: ['./url-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgxMessagingSharedModule, NgxMaterialIntlTelInputComponent],
})
export class UrlGeneratorComponent {
  private fb = inject(FormBuilder);
  private darkModeService = inject(DarkModeService);
  private httpClient = inject(HttpClient);
  private snackBarService = inject(SnackBarService);
  private logger = inject(LoggerService);
  private dataService = inject(DataService);

  newForm: FormGroup;
  phoneControl: FormControl;

  @Input() phoneNumber!: string;
  @Input() accountUID!: string;
  @Input() subAccountUID!: string;
  @Input() apiKey!: string;
  @Input() secretKey!: string;
  @Input() displayName!: string;
  @Input() updateInfo!: boolean;
  @Input() readOnly!: boolean;
  @Input() ownerId!: string;
  @Input() ownerDisplayName!: string;
  @Input() startDate!: number;
  @Input() endDate!: number;
  @Input() temporaryDisplayName!: string;
  @Input() primaryColor!: string;
  @Input() secondaryColor!: string;
  @Input() messageTextColor!: string;
  @Input() messageBubbleColor!: string;
  @Input() timestampColor!: string;
  @Input() senderTimestampColor!: string;
  @Input() placeholderTextColor!: string;
  @Input() placeholderBackgroundColor!: string;
  @Input() inputBackgroundColor!: string;
  @Input() inputTextColor!: string;
  @Input() senderBubbleColor!: string;
  @Input() senderTextColor!: string;
  @Input() backgroundColor!: string;
  @Input() headerBackgroundColor!: string;
  @Input() headerTextColor!: string;
  @Input() tags!: string;
  @Input() messagesTags!: string;
  @Input() hideAttachments!: boolean;
  @Input() hideEditPhone!: boolean;
  @Input() showChannelInfo!: boolean;
  @Input() view!: string;

  readonly colorFields = [
    'primaryColor',
    'secondaryColor',
    'messageTextColor',
    'messageBubbleColor',
    'timestampColor',
    'senderTimestampColor',
    'placeholderTextColor',
    'placeholderBackgroundColor',
    'inputBackgroundColor',
    'inputTextColor',
    'senderBubbleColor',
    'senderTextColor',
    'backgroundColor',
    'headerBackgroundColor',
    'headerTextColor',
  ] as const;

  readonly checkboxFields = [
    { key: 'updateInfo', label: 'updateInfo' },
    { key: 'readOnly', label: 'readOnly' },
    { key: 'hideAttachments', label: 'hideAttachments' },
    { key: 'hideEditPhone', label: 'hideEditPhone' },
    { key: 'showChannelInfo', label: 'showChannelInfo' },
  ] as const;

  submitted = signal(false);
  externalUrl = signal<string | null>(null);
  isGenerating = signal(false);
  generateError = signal<string | null>(null);

  ngOnInit(): void {
    this.darkModeService.toggleDarkmode(false);
    this.setForm();
  }

  setForm(): void {
    this.phoneControl = this.fb.control(this.initialPhoneNumber(this.phoneNumber), [Validators.required, validPhoneNumber]);
    this.newForm = this.fb.group({
      phoneNumber: this.phoneControl,
      accountUID: [this.accountUID, [Validators.required]],
      subAccountUID: [this.subAccountUID, [Validators.required]],
      routing: ['SMS', [Validators.required]],
      apiKey: [this.apiKey, [Validators.required]],
      secretKey: [this.secretKey, [Validators.required]],
      displayName: [this.displayName, [Validators.required]],
      updateInfo: [this.updateInfo],
      readOnly: [this.readOnly],
      ownerId: [this.ownerId],
      ownerDisplayName: [this.ownerDisplayName],
      startDate: [this.startDate ? this.dataService.convertTimestampToDate(Number(this.startDate)) : null],
      endDate: [this.endDate ? this.dataService.convertTimestampToDate(Number(this.endDate)) : null],
      primaryColor: [this.dataService.formatColorStringToColorHex(this.primaryColor)],
      messageTextColor: [this.dataService.formatColorStringToColorHex(this.messageTextColor)],
      messageBubbleColor: [this.dataService.formatColorStringToColorHex(this.messageBubbleColor)],
      timestampColor: [this.dataService.formatColorStringToColorHex(this.timestampColor)],
      senderTimestampColor: [this.dataService.formatColorStringToColorHex(this.senderTimestampColor)],
      placeholderTextColor: [this.dataService.formatColorStringToColorHex(this.placeholderTextColor)],
      placeholderBackgroundColor: [this.dataService.formatColorStringToColorHex(this.placeholderBackgroundColor)],
      inputBackgroundColor: [this.dataService.formatColorStringToColorHex(this.inputBackgroundColor)],
      inputTextColor: [this.dataService.formatColorStringToColorHex(this.inputTextColor)],
      senderBubbleColor: [this.dataService.formatColorStringToColorHex(this.senderBubbleColor)],
      senderTextColor: [this.dataService.formatColorStringToColorHex(this.senderTextColor)],
      backgroundColor: [this.dataService.formatColorStringToColorHex(this.backgroundColor)],
      headerBackgroundColor: [this.dataService.formatColorStringToColorHex(this.headerBackgroundColor)],
      headerTextColor: [this.dataService.formatColorStringToColorHex(this.headerTextColor)],
      secondaryColor: [this.dataService.formatColorStringToColorHex(this.secondaryColor)],
      tags: this.fb.array([]),
      messagesTags: this.fb.array([]),
      temporaryDisplayName: [this.temporaryDisplayName],
      hideAttachments: [this.hideAttachments],
      hideEditPhone: [this.hideEditPhone],
      showChannelInfo: [this.showChannelInfo],
      view: [this.view],
    });
    this.setTags();
    this.setColorsFormSubscriptions();
  }

  setTags(): void {
    const tags = this.dataService.formatTagsStringToTagsArray(this.tags);
    for (let index = 0; index < tags.length; index++) {
      this.addTag(tags[index], 'tags');
    }
    const messagesTags = this.dataService.formatTagsStringToTagsArray(this.messagesTags);
    for (let index = 0; index < messagesTags.length; index++) {
      this.addTag(messagesTags[index], 'messagesTags');
    }
  }

  setColorsFormSubscriptions(): void {
    for (const key in this.newForm.value) {
      if (key.toUpperCase().includes('COLOR')) {
        this.setupColorSync(key);
      }
    }
  }

  setupColorSync(controlName: string) {
    const control = this.newForm.get(controlName);
    if (!control) {
      return;
    }
    control.valueChanges.subscribe((value) => {
      control.setValue(value, { emitEvent: false });
    });
  }

  fetchExternalUrl(apiUrl: string, payload: ISignaturePayload, headers: ISignatureHeaders): Observable<string> {
    return this.httpClient.post<ISignatureData>(apiUrl, payload, { headers, context: skipApiKey() }).pipe(
      map((response) => {
        let url = environment.urlMesaging + '#/EX/channel?';
        url = url + 'signature=' + response.canonicalSignature;
        url = url + '&timestamp=' + response.timestamp;
        url = this.getUrl(url, this.newForm.value as IUrlGeneratorFormParams);
        return url;
      }),
      catchError((error: HttpErrorResponse) => {
        const body = error.error as { message?: string } | string | null;
        const message =
          (typeof body === 'string' && body) ||
          (typeof body === 'object' && body?.message) ||
          error.message ||
          `Signature request failed (${error.status})`;
        return throwError(() => new Error(message));
      })
    );
  }

  generateExternalURL(): void {
    if (!this.validateRequiredFields()) {
      return;
    }
    if (!environment.externalApi) {
      this.generateError.set('externalApi not configured for this environment');
      return;
    }

    const apiUrl = `${environment.externalApi}/apiExternal/signature`;
    const headers: ISignatureHeaders = {
      'x-authorization-api-key': this.newForm.value.apiKey as string,
      'x-authorization-timestamp': Date.now().toString(),
    };
    let customerPhoneNumber: string;
    try {
      customerPhoneNumber = this.dataService.setPhoneNumberToE164(this.newForm.value.phoneNumber as string);
    } catch (error) {
      this.generateError.set('phoneNumber is invalid');
      this.logger.error(error, 'UrlGenerator.generateExternalURL');
      return;
    }

    const payload: ISignaturePayload = {
      apiKey: this.newForm.value.apiKey as string,
      secretKey: this.newForm.value.secretKey as string,
      payload: {
        accountUID: this.newForm.value.accountUID as string,
        subAccountUID: this.newForm.value.subAccountUID as string,
        customerPhoneNumber,
      },
      uri: '/signature/validate',
      method: 'POST',
    };

    this.isGenerating.set(true);
    this.generateError.set(null);
    this.externalUrl.set(null);
    this.fetchExternalUrl(apiUrl, payload, headers).subscribe({
      next: (url) => {
        this.externalUrl.set(url);
        this.isGenerating.set(false);
      },
      error: (error: unknown) => {
        this.generateError.set(this.toErrorMessage(error));
        this.isGenerating.set(false);
        this.logger.error(error, 'UrlGenerator.generateExternalURL');
      },
    });
  }

  copyFormDataToClipboard(): Promise<void> {
    if (!this.validateRequiredFields()) {
      return Promise.resolve();
    }
    return this.writeFormUrlToClipboard();
  }

  showError(controlName: string): boolean {
    const control = this.newForm?.get(controlName);
    return !!control && control.invalid && (control.touched || this.submitted());
  }

  fieldError(controlName: string): string {
    const control = this.newForm?.get(controlName);
    if (!control?.errors) {
      return '';
    }
    if (control.errors['required']) {
      return `${this.fieldLabel(controlName)} is required`;
    }
    return `${this.fieldLabel(controlName)} is invalid`;
  }

  fieldLabel(key: string): string {
    const acronyms: Record<string, string> = { uid: 'UID', id: 'ID', api: 'API' };
    return key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .split(' ')
      .map((word) => acronyms[word.toLowerCase()] ?? `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
      .join(' ');
  }

  private initialPhoneNumber(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    if (!trimmed) {
      return null;
    }
    return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
  }

  private toErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const body = error.error as { message?: string } | string | null;
      return (
        (typeof body === 'string' && body) ||
        (typeof body === 'object' && body?.message) ||
        error.message ||
        `Signature request failed (${error.status})`
      );
    }
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return 'Error generating url';
  }

  private validateRequiredFields(): boolean {
    this.submitted.set(true);
    this.newForm.markAllAsTouched();
    if (this.newForm.valid) {
      return true;
    }
    this.scrollToFirstError();
    return false;
  }

  private scrollToFirstError(): void {
    queueMicrotask(() => {
      const scroller = document.querySelector('.page-container');
      const field = document.querySelector('.field__control--invalid');
      if (!scroller || !field) {
        return;
      }
      const offset = field.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      scroller.scrollTo({ top: scroller.scrollTop + offset - 24, behavior: 'smooth' });
    });
  }

  getUrl(urlBase: string, data: IUrlGeneratorFormParams): string {
    for (const key in data) {
      if (data[key]) {
        if (key.toUpperCase().includes('COLOR')) {
          urlBase = urlBase + '&' + key + '=' + this.dataService.deleteTagForColor(data[key] as string);
        } else if (key.toUpperCase().includes('TAG')) {
          urlBase = urlBase + '&' + key + '=' + this.dataService.formatTagsArrayToString(data[key] as string[]);
        } else if (key.toUpperCase().includes('PHONE') && key.toUpperCase() !== 'HIDEEDITPHONE') {
          urlBase = urlBase + '&' + key + '=' + this.dataService.setPhoneNumberToE164(data[key] as string);
        } else if (key.toUpperCase().includes('DATE') && key.toUpperCase() !== 'UPDATEINFO') {
          urlBase = urlBase + '&' + key + '=' + this.dataService.setDatesToTimestamp(new Date(data[key] as string), key);
        } else {
          urlBase = urlBase + '&' + key + '=' + data[key];
        }
      }
    }
    return urlBase;
  }

  private async writeFormUrlToClipboard(): Promise<void> {
    const url = environment.urlMesaging + '#/url-generator?';
    const data = this.newForm.value as IUrlGeneratorFormParams;
    const text = this.getUrl(url, data);
    try {
      await navigator.clipboard.writeText(text);
      this.snackBarService.showSuccesSnackbar('copied to clipboard');
    } catch (err) {
      this.snackBarService.showErrorSnackBar('error copying to clipboard');
      this.logger.error(err, 'UrlGenerator.copyFormDataToClipboard');
    }
  }

  // Tags
  addTag(tag: string, key: string) {
    this.tagsForm(key).push(this.fb.control(tag));
  }

  removeTag(index: number, key: string) {
    const value = this.tagsForm(key).value as string[];
    this.tagsForm(key).setValue(
      value
        .slice(0, index)
        .concat(value.slice(index + 1))
        .concat(value[index])
    );
    this.tagsForm(key).removeAt(value.length - 1);
  }

  tagsForm(key: string): FormArray {
    return this.newForm.get(key) as FormArray;
  }

  toDateInput(value: Date | string | null | undefined): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  setDate(controlName: 'startDate' | 'endDate', event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.newForm.get(controlName)?.setValue(value ? new Date(`${value}T00:00:00`) : null);
  }

  colorPickerValue(controlName: string): string {
    const value = this.newForm.get(controlName)?.value as string | null;
    return /^#[0-9a-fA-F]{6}$/.test(value ?? '') ? (value as string) : '#000000';
  }

  setColor(controlName: string, event: Event): void {
    this.newForm.get(controlName)?.setValue((event.target as HTMLInputElement).value);
  }

  // end tags
}
